import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, X, CalendarDays, ChevronRight, Trash2, CheckCircle } from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-zinc-100 text-zinc-700",
  on_track: "bg-emerald-100 text-emerald-800",
  at_risk: "bg-amber-100 text-amber-800",
  delayed: "bg-red-100 text-red-700",
  complete: "bg-blue-100 text-blue-800",
};

export default function TACalendar() {
  const { user } = useAuth();
  const [milestones, setMilestones] = useState([]);
  const [collections, setCollections] = useState([]);
  const [calendars, setCalendars] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", planned_date: "", status: "pending" });
  const [selectedCollection, setSelectedCollection] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => {
    Promise.all([
      api.get("/calendar/milestones").then((r) => setMilestones(r.data)).catch(() => setMilestones([])),
      api.get("/collections").then((r) => setCollections(r.data)).catch(() => setCollections([])),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const ensureCalendar = async (collectionId) => {
    if (calendars[collectionId]) return calendars[collectionId];
    try {
      const r = await api.get(`/collections/${collectionId}/calendar`);
      setCalendars((prev) => ({ ...prev, [collectionId]: r.data.id }));
      return r.data.id;
    } catch {
      const r = await api.post(`/collections/${collectionId}/calendar`, { status: "draft" });
      setCalendars((prev) => ({ ...prev, [collectionId]: r.data.id }));
      return r.data.id;
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", planned_date: "", status: "pending" });
    setSelectedCollection(collections[0]?.id || "");
    setShowModal(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    const dateStr = m.planned_date ? m.planned_date.split("T")[0] : "";
    setForm({ name: m.name, planned_date: dateStr, status: m.status });
    setShowModal(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/milestones/${editing.id}`, {
          name: form.name,
          planned_date: form.planned_date ? new Date(form.planned_date).toISOString() : undefined,
          status: form.status,
        });
        toast.success("Milestone updated");
      } else {
        if (!selectedCollection) {
          toast.error("Select a collection first");
          setSaving(false);
          return;
        }
        const calendarId = await ensureCalendar(selectedCollection);
        await api.post(`/calendars/${calendarId}/milestones`, {
          name: form.name,
          planned_date: new Date(form.planned_date).toISOString(),
          owner_id: user?.id,
        });
        toast.success("Milestone created");
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteMilestone = async (id) => {
    if (!window.confirm("Delete this milestone?")) return;
    try {
      await api.delete(`/milestones/${id}`);
      toast.success("Milestone deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const completeMilestone = async (m) => {
    try {
      await api.patch(`/milestones/${m.id}`, {
        status: "complete",
        actual_date: new Date().toISOString(),
      });
      toast.success("Milestone marked complete");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const filteredMilestones = milestones.filter((m) => {
    if (filter === "all") return true;
    if (filter === "overdue") {
      const d = new Date(m.planned_date);
      return m.status !== "complete" && d < new Date();
    }
    return m.status === filter;
  });

  const dates = milestones.map((m) => new Date(m.planned_date)).filter((d) => !isNaN(d));
  const minDate = dates.length ? new Date(Math.min(...dates)) : new Date();
  const maxDate = dates.length ? new Date(Math.max(...dates)) : new Date();
  const rangeMs = Math.max(1, maxDate - minDate);

  const dotPosition = (m) => {
    const d = new Date(m.planned_date);
    if (isNaN(d)) return "0%";
    return `${((d - minDate) / rangeMs) * 100}%`;
  };

  const barColor = (status) => {
    const colors = { pending: "bg-zinc-400", on_track: "bg-emerald-500", at_risk: "bg-amber-500", delayed: "bg-red-500", complete: "bg-blue-500" };
    return colors[status] || "bg-zinc-400";
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Time & Action Calendar</h1>
          <p className="text-zinc-500 mt-2 text-sm">Track milestones across your production timeline.</p>
        </div>
        <button onClick={openCreate}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> Add milestone
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : milestones.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
          <CalendarDays size={26} className="mx-auto mb-3" />
          <p className="text-sm">No milestones yet.</p>
        </div>
      ) : (
        <>
          <div className="mt-8 border border-zinc-200 bg-white rounded-lg p-6">
            <h3 className="text-sm font-semibold mb-4">Timeline</h3>
            <div className="space-y-3">
              {milestones.map((m) => (
                <div key={m.id} className="flex items-center gap-4">
                  <span className="text-xs text-zinc-600 w-40 truncate">{m.name}</span>
                  <div className="flex-1 relative h-6 bg-zinc-50 rounded">
                    <div className={`absolute w-3 h-3 rounded-full top-1.5 -ml-1.5 ${barColor(m.status)}`}
                      style={{ left: dotPosition(m) }} title={m.planned_date?.split("T")[0]} />
                  </div>
                  {m.collection_name && <span className="text-[10px] text-zinc-400 w-24 truncate text-right">{m.collection_name}</span>}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-zinc-400">
              <span>{minDate.toLocaleDateString()}</span>
              <span>{maxDate.toLocaleDateString()}</span>
            </div>
          </div>

          {/* Status filter */}
          <div className="mt-6 flex gap-2">
            {["all", "pending", "on_track", "at_risk", "delayed", "complete", "overdue"].map((s) => (
              <button key={s} onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs capitalize ${filter === s ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                {s === "all" ? "All" : s.replace("_", " ")}
              </button>
            ))}
          </div>

          <div className="mt-4 border border-zinc-200 bg-white rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="col-span-3">Milestone</span>
              <span className="col-span-2">Planned Date</span>
              <span className="col-span-2">Collection</span>
              <span className="col-span-2">Status</span>
              <span className="col-span-3 text-right">Actions</span>
            </div>
            {filteredMilestones.map((m) => {
              const isOverdue = m.status !== "complete" && m.planned_date && new Date(m.planned_date) < new Date();
              return (
              <div key={m.id} className={`grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50 ${isOverdue ? "bg-red-50/50" : ""}`}>
                <span className="col-span-3 text-sm font-medium">{m.name}</span>
                <span className={`col-span-2 text-sm ${isOverdue ? "text-red-600 font-medium" : "text-zinc-600"}`}>{m.planned_date ? new Date(m.planned_date).toLocaleDateString() : "—"}</span>
                <span className="col-span-2 text-xs text-zinc-500">{m.collection_name || "—"}</span>
                <span className="col-span-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[m.status] || STATUS_STYLE.pending}`}>
                    {(m.status || "pending").replace("_", " ")}
                  </span>
                </span>
                <span className="col-span-3 text-right flex items-center justify-end gap-1">
                  {m.status !== "complete" && (
                    <button onClick={() => completeMilestone(m)} className="p-1.5 hover:bg-emerald-50 rounded-md text-emerald-500" title="Mark complete">
                      <CheckCircle size={14} />
                    </button>
                  )}
                  <button onClick={() => openEdit(m)} className="text-xs text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-0.5">
                    Edit <ChevronRight size={12} />
                  </button>
                  <button onClick={() => deleteMilestone(m.id)} className="p-1.5 hover:bg-red-50 rounded-md text-red-400" title="Delete">
                    <Trash2 size={14} />
                  </button>
                </span>
              </div>
              );
            })}
          </div>
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">{editing ? "Edit milestone" : "New milestone"}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Milestone name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              {!editing && (
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Collection</label>
                  <select value={selectedCollection} onChange={(e) => setSelectedCollection(e.target.value)}
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                    <option value="">Select a collection</option>
                    {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Planned date</label>
                <input type="date" value={form.planned_date} onChange={(e) => setForm({ ...form, planned_date: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="pending">Pending</option>
                <option value="on_track">On Track</option>
                <option value="at_risk">At Risk</option>
                <option value="delayed">Delayed</option>
                <option value="complete">Complete</option>
              </select>
              <button onClick={save} disabled={!form.name || !form.planned_date || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Saving..." : editing ? "Update milestone" : "Create milestone"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
